// Pure helpers voor het Inkoop-besteltraject (batch-4 inkoop).
//
// Géén DB-calls, géén Fastify, géén `node:*`. Sequentie-codes (aanvraag/RFQ),
// het besteladvies bij minimumvoorraad (Deel 8) en de goedkeurings-cascade op
// basis van grensbedragen + budget (Deel 11) leven hier zodat ze in de API én
// in tests deterministisch zijn. Geld in deze module is `number` (euro, 2
// decimalen); de route zet om van/naar Prisma `Decimal`.

// ────────────────────────────────────────────────────────────────────────────
// Sequentie-codes — per kalenderjaar, herkenbaar zoals PO/FAC
// ────────────────────────────────────────────────────────────────────────────

/** Bouw het volgende aanvraagnummer "IA-YYYY-NNNNN" (5-cijferig). */
export function buildAanvraagNummer(year: number, highestN: number): string {
  const padded = String(highestN + 1).padStart(5, '0')
  return `IA-${year}-${padded}`
}

/** Extraheer het sequentienummer uit "IA-2026-00123"; 0 als het niet matcht. */
export function parseAanvraagNummer(code: string): number {
  const match = code.match(/^IA-\d{4}-(\d+)$/)
  if (!match) return 0
  const n = Number.parseInt(match[1] ?? '0', 10)
  return Number.isFinite(n) ? n : 0
}

/** Bouw het volgende offerteaanvraag/RFQ-nummer "RFQ-YYYY-NNNNN" (5-cijferig). */
export function buildRfqNummer(year: number, highestN: number): string {
  const padded = String(highestN + 1).padStart(5, '0')
  return `RFQ-${year}-${padded}`
}

/** Extraheer het sequentienummer uit "RFQ-2026-00045"; 0 als het niet matcht. */
export function parseRfqNummer(code: string): number {
  const match = code.match(/^RFQ-\d{4}-(\d+)$/)
  if (!match) return 0
  const n = Number.parseInt(match[1] ?? '0', 10)
  return Number.isFinite(n) ? n : 0
}

// ────────────────────────────────────────────────────────────────────────────
// Besteladvies — minimumvoorraad -> inkoopvoorstel (Deel 8)
// ────────────────────────────────────────────────────────────────────────────

export interface BesteladviesInput {
  /** Huidige fysieke voorraad (som saldi). */
  huidigeVoorraad: number
  /** Minimumvoorraad-drempel op het artikel. */
  minimumVoorraad: number
  /** Al lopende bestellingen (besteld maar nog niet ontvangen). */
  reedsBesteld: number
  /** Standaard bestelhoeveelheid op het artikel (optioneel). */
  bestelAantal?: number | null
  /** Gemiddeld verbruik per maand (laatste 3 mnd), optioneel ter verfijning. */
  gemiddeldVerbruikPerMaand?: number | null
  /** Levertijd leverancier in dagen, optioneel ter verfijning. */
  levertijdDagen?: number | null
  /** Extra veiligheidsvoorraad bovenop het minimum (optioneel). */
  veiligheidsvoorraad?: number | null
}

export interface Besteladvies {
  /** Geadviseerd bestelaantal (>= 0; 0 = niets nodig). */
  adviesAantal: number
  /** Tekort t.o.v. minimum (+ veiligheid) na aftrek van reeds bestelde. */
  tekort: number
  /** Verwacht verbruik gedurende de levertijd (0 als onbekend). */
  verbruikTijdensLevertijd: number
}

/**
 * Besteladvies = (benodigd tot nieuwe levering + veiligheidsvoorraad) -
 * huidige voorraad - reeds besteld, verfijnd met gemiddeld verbruik tijdens de
 * levertijd. Pure functie: de route haalt de waarden op, deze functie rekent.
 *
 * - `tekort` dekt het gat tot minimum + veiligheidsvoorraad.
 * - `verbruikTijdensLevertijd` schat wat er tijdens de levertijd nog opgaat
 *   (gemiddeldVerbruikPerMaand * levertijdDagen/30), zodat de bestelling niet
 *   meteen weer onder minimum zakt.
 * - `adviesAantal`: bij een ingesteld `bestelAantal` is dat de bodem (ronde
 *   besteleenheid), anders het afgeronde tekort + verbruik. Nooit negatief.
 */
export function berekenBesteladvies(input: BesteladviesInput): Besteladvies {
  const veiligheid = Math.max(0, input.veiligheidsvoorraad ?? 0)
  const drempel = input.minimumVoorraad + veiligheid
  const tekort = Math.max(0, drempel - input.huidigeVoorraad - input.reedsBesteld)

  const verbruikPerMaand = Math.max(0, input.gemiddeldVerbruikPerMaand ?? 0)
  const levertijdDagen = Math.max(0, input.levertijdDagen ?? 0)
  const verbruikTijdensLevertijd = Math.ceil((verbruikPerMaand * levertijdDagen) / 30)

  if (tekort <= 0) {
    return { adviesAantal: 0, tekort: 0, verbruikTijdensLevertijd }
  }

  const benodigd = Math.ceil(tekort + verbruikTijdensLevertijd)
  const adviesAantal =
    input.bestelAantal && input.bestelAantal > 0
      ? Math.max(input.bestelAantal, benodigd)
      : Math.max(1, benodigd)

  return { adviesAantal, tekort: Math.ceil(tekort), verbruikTijdensLevertijd }
}

// ────────────────────────────────────────────────────────────────────────────
// Goedkeurings-cascade — grensbedragen + budget (Deel 11)
// ────────────────────────────────────────────────────────────────────────────

export type InkoopGoedkeuringStatusValue =
  | 'GEEN'
  | 'WACHT_OP_TEAMLEIDER'
  | 'WACHT_OP_KASPER'
  | 'WACHT_OP_DIRECTIE'
  | 'WACHT_OP_FINANCE'
  | 'GOEDGEKEURD'
  | 'AFGEWEZEN'

export interface GoedkeuringInput {
  /** Geschat/bevestigd bedrag van de aanvraag/bestelling (excl. btw). */
  bedrag: number
  /** Tot dit bedrag mag de aanvrager zonder extra goedkeuring (null = onbeperkt). */
  magAanvragenTot?: number | null
  /** Tot dit bedrag volstaat de teamleider; daarboven Kasper/directie (null = onbeperkt). */
  magGoedkeurenTotTeamleider?: number | null
  /** Vanaf dit bedrag is directie-/finance-goedkeuring vereist (null = nooit). */
  extraGoedkeuringVanaf?: number | null
  /** Betreft een nieuw (nog niet bestaand) artikel. */
  nieuwArtikel?: boolean
  /** Betreft een nieuwe/onbekende leverancier. */
  nieuweLeverancier?: boolean
  /** Grensbedrag-vlag: altijd goedkeuring bij een nieuw artikel. */
  goedkeuringBijNieuwArtikel?: boolean
  /** Grensbedrag-vlag: altijd goedkeuring bij een nieuwe leverancier. */
  goedkeuringBijNieuweLeverancier?: boolean
  /** Budget (dreigt te) worden overschreden -> finance erbij. */
  budgetOverschreden?: boolean
}

export interface GoedkeuringUitkomst {
  status: InkoopGoedkeuringStatusValue
  /** Of er überhaupt extra goedkeuring nodig is. */
  vereistGoedkeuring: boolean
  /** Mens-leesbare redenen (voor UI + audit). Geen em-dash. */
  redenen: string[]
}

// Severity-ordening zodat we de zwaarste vereiste stap kiezen.
const STAP_SEVERITY: Record<InkoopGoedkeuringStatusValue, number> = {
  GEEN: 0,
  WACHT_OP_TEAMLEIDER: 1,
  WACHT_OP_KASPER: 2,
  WACHT_OP_FINANCE: 3,
  WACHT_OP_DIRECTIE: 4,
  GOEDGEKEURD: 5,
  AFGEWEZEN: 5,
}

/**
 * Bepaalt de vereiste goedkeuringsstap op basis van het bedrag t.o.v. de
 * grensbedragen + de nieuw-artikel/nieuwe-leverancier/budget-triggers.
 *
 * Cascade (de zwaarste van de toepasselijke stappen wint):
 *   - bedrag <= magAanvragenTot en geen trigger  -> GEEN
 *   - boven het aanvraagmandaat of een trigger    -> minstens WACHT_OP_TEAMLEIDER
 *   - boven het teamleider-mandaat                 -> WACHT_OP_KASPER
 *   - budget overschreden                          -> WACHT_OP_FINANCE
 *   - bedrag >= extraGoedkeuringVanaf              -> WACHT_OP_DIRECTIE
 *
 * Serverside leidend: de route gebruikt deze functie om de stap te forceren,
 * niet alleen de UI.
 */
export function bepaalInkoopGoedkeuring(input: GoedkeuringInput): GoedkeuringUitkomst {
  const redenen: string[] = []
  let stap: InkoopGoedkeuringStatusValue = 'GEEN'

  const verhoogNaar = (doel: InkoopGoedkeuringStatusValue, reden: string) => {
    if (STAP_SEVERITY[doel] > STAP_SEVERITY[stap]) stap = doel
    redenen.push(reden)
  }

  const aanvraagMandaat = input.magAanvragenTot ?? Number.POSITIVE_INFINITY
  if (input.bedrag > aanvraagMandaat) {
    verhoogNaar(
      'WACHT_OP_TEAMLEIDER',
      `Bedrag (${input.bedrag}) boven het aanvraagmandaat (${input.magAanvragenTot}).`,
    )
  }

  if (input.nieuwArtikel && input.goedkeuringBijNieuwArtikel) {
    verhoogNaar('WACHT_OP_TEAMLEIDER', 'Nieuw artikel vereist goedkeuring.')
  }
  if (input.nieuweLeverancier && input.goedkeuringBijNieuweLeverancier) {
    verhoogNaar('WACHT_OP_TEAMLEIDER', 'Nieuwe leverancier vereist goedkeuring.')
  }

  const tlMandaat = input.magGoedkeurenTotTeamleider ?? Number.POSITIVE_INFINITY
  if (input.bedrag > tlMandaat) {
    verhoogNaar(
      'WACHT_OP_KASPER',
      `Bedrag (${input.bedrag}) boven het teamleider-mandaat (${input.magGoedkeurenTotTeamleider}).`,
    )
  }

  if (input.budgetOverschreden) {
    verhoogNaar('WACHT_OP_FINANCE', 'Budget (dreigt) overschreden; finance vereist.')
  }

  const directieVanaf = input.extraGoedkeuringVanaf ?? Number.POSITIVE_INFINITY
  if (input.bedrag >= directieVanaf) {
    verhoogNaar(
      'WACHT_OP_DIRECTIE',
      `Bedrag (${input.bedrag}) op/boven de directie-grens (${input.extraGoedkeuringVanaf}).`,
    )
  }

  return {
    status: stap,
    vereistGoedkeuring: stap !== 'GEEN',
    redenen,
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Open-aantal van een orderregel (deelleveringen)
// ────────────────────────────────────────────────────────────────────────────

/** Open = besteld - reeds ontvangen, ondergrens 0. */
export function openAantal(besteld: number, reedsOntvangen: number): number {
  return Math.max(0, besteld - reedsOntvangen)
}

/**
 * Bepaalt of een order na deze ontvangst volledig of deels geleverd is.
 * `regels` = per orderregel het bestelde + (cumulatief) ontvangen aantal.
 */
export function ontvangstVolledig(
  regels: Array<{ besteld: number; ontvangen: number }>,
): boolean {
  if (regels.length === 0) return false
  return regels.every((r) => r.ontvangen >= r.besteld)
}

// ────────────────────────────────────────────────────────────────────────────
// Leverancier-status bestel-gate (batch-12 FO-002)
// ────────────────────────────────────────────────────────────────────────────

export type LeverancierStatusValue = 'IN_AFWACHTING' | 'GOEDGEKEURD' | 'VOORKEUR' | 'GEBLOKKEERD'

/** Context van de gate-check: een echte bestelling, of een offerte-aanvraag (RFQ). */
export type LeverancierBestelContext = 'BESTELLEN' | 'OFFERTE'

export interface LeverancierBestelGateUitkomst {
  toegestaan: boolean
  /** Foutcode voor de API-respons (alleen bij !toegestaan). */
  code?: 'LEVERANCIER_GEBLOKKEERD' | 'LEVERANCIER_NIET_GOEDGEKEURD'
  /** Mens-leesbare melding (geen em-dash). */
  message?: string
}

/**
 * Bepaalt of er bij een leverancier besteld/geofferd mag worden op basis van de
 * leverancier-status. Eén mechanisme voor de hele bestel-gate (FO-002 §6/§10):
 *   - GEBLOKKEERD   -> nooit (met reden); ook geen offerte-aanvraag.
 *   - IN_AFWACHTING -> bij BESTELLEN geweigerd ("eerst goedkeuren"); bij OFFERTE
 *     toegestaan (een nieuwe aanbieder mag je offerte vragen vóór goedkeuring).
 *   - GOEDGEKEURD / VOORKEUR -> toegestaan.
 * `status` null = legacy/onbekend -> behandeld als IN_AFWACHTING (veilig).
 * Serverside leidend: de routes roepen deze functie aan, niet alleen de UI.
 */
export function bepaalLeverancierBestelGate(
  status: LeverancierStatusValue | null | undefined,
  context: LeverancierBestelContext,
  blokkadeReden?: string | null,
): LeverancierBestelGateUitkomst {
  const s = status ?? 'IN_AFWACHTING'

  if (s === 'GEBLOKKEERD') {
    const reden = blokkadeReden?.trim()
    return {
      toegestaan: false,
      code: 'LEVERANCIER_GEBLOKKEERD',
      message: reden
        ? `Deze leverancier is geblokkeerd: ${reden}`
        : 'Deze leverancier is geblokkeerd; bestellen is niet toegestaan.',
    }
  }

  if (s === 'GOEDGEKEURD' || s === 'VOORKEUR') {
    return { toegestaan: true }
  }

  // IN_AFWACHTING: offerte vragen mag, bestellen niet.
  if (context === 'OFFERTE') {
    return { toegestaan: true }
  }
  return {
    toegestaan: false,
    code: 'LEVERANCIER_NIET_GOEDGEKEURD',
    message:
      'Deze leverancier is nog niet goedgekeurd; keur de leverancier eerst goed voordat je bestelt.',
  }
}

// ────────────────────────────────────────────────────────────────────────────
// FO-079 (batch-20) — offertetraject: weergave-status + totalen uit de regels
// ────────────────────────────────────────────────────────────────────────────

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

/**
 * Afgeleide weergave-status van een leverancier-offerte (geen DB-kolom): leidt uit
 * de bestaande velden `goedgekeurd`/`geldigTot` (+ of er inhoud is) één van vijf
 * statussen af. `goedgekeurd === true` = gekozen, `false` = afgewezen; beide winnen
 * van "verlopen" (een keuze blijft een keuze, ook na de geldigheidsdatum).
 */
export type OfferteWeergaveStatus = 'CONCEPT' | 'ONTVANGEN' | 'GEKOZEN' | 'AFGEWEZEN' | 'VERLOPEN'

export interface OfferteWeergaveInput {
  /** `true` = gekozen, `false` = afgewezen, `null` = nog niet beoordeeld. */
  goedgekeurd: boolean | null
  /** Geldig-tot als kalenderdag (yyyy-MM-dd, Europe/Amsterdam) of null. */
  geldigTotYmd: string | null
  /** Of de offerte inhoud heeft (regels of een offertebedrag). */
  heeftInhoud: boolean
}

export function offerteWeergaveStatus(
  o: OfferteWeergaveInput,
  vandaagYmd: string,
): OfferteWeergaveStatus {
  if (o.goedgekeurd === true) return 'GEKOZEN'
  if (o.goedgekeurd === false) return 'AFGEWEZEN'
  // Nog niet beoordeeld: verlopen wint van ontvangen/concept.
  if (o.geldigTotYmd && o.geldigTotYmd < vandaagYmd) return 'VERLOPEN'
  if (!o.heeftInhoud) return 'CONCEPT'
  return 'ONTVANGEN'
}

export const OFFERTE_WEERGAVE_STATUS_LABELS: Record<OfferteWeergaveStatus, string> = {
  CONCEPT: 'Concept',
  ONTVANGEN: 'Ontvangen',
  GEKOZEN: 'Gekozen',
  AFGEWEZEN: 'Afgewezen',
  VERLOPEN: 'Verlopen',
}

export interface OfferteRegelBedrag {
  aantal: number
  prijsPerStuk: number
  /** Btw als percentage (21 = 21%), niet als fractie. */
  btwPercentage: number
}

export interface OfferteTotalen {
  exclBtw: number
  btwBedrag: number
  inclBtw: number
}

/**
 * Totaal excl/incl btw uit de offerte-regels (per regel `aantal x prijsPerStuk`,
 * btw per regel-percentage). Rondt pas op het laatste moment af (cent-precisie),
 * zodat `exclBtw + btwBedrag === inclBtw`. Pure functie; de route zet Decimal om.
 */
export function berekenOfferteTotalen(regels: ReadonlyArray<OfferteRegelBedrag>): OfferteTotalen {
  let excl = 0
  let btw = 0
  for (const r of regels) {
    const sub = r.aantal * r.prijsPerStuk
    excl += sub
    btw += sub * (r.btwPercentage / 100)
  }
  const exclBtw = round2(excl)
  const btwBedrag = round2(btw)
  return { exclBtw, btwBedrag, inclBtw: round2(exclBtw + btwBedrag) }
}
